import * as React from 'react';
import type { CurrentUserResponse } from '@klareospace/pharos-lib-types';
import { AnimatedHeight } from './animated-height';
import { JumpToResults } from './jump-to-results';
import { SearchBar } from './search-bar';
import { SitesPanel } from './sites-panel';
import { SpacesPanel } from './spaces-panel';
import { TenantRow } from './tenant-row';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PanelView = 'spaces' | 'sites';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MainSelectorDropdownContentProps {
  backendUser: CurrentUserResponse | null;
  browsedSpaceId: string | null;
  browsedSpaceName: string | null;
  canReadSites: boolean;
  canReadSpaces: boolean;
  canSelectSpace: boolean;
  debouncedQuery: string;
  isSingleSpaceImposed: boolean;
  onBrowseSpace: (spaceId: string, spaceName: string) => void;
  onClose: () => void;
  onGoBackToSpaces: () => void;
  onSearchChange: (query: string) => void;
  panel: PanelView;
  searchQuery: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MainSelectorDropdownContent({
  backendUser,
  browsedSpaceId,
  browsedSpaceName,
  canReadSites,
  canReadSpaces,
  canSelectSpace,
  debouncedQuery,
  isSingleSpaceImposed,
  onBrowseSpace,
  onClose,
  onGoBackToSpaces,
  onSearchChange,
  panel,
  searchQuery,
}: MainSelectorDropdownContentProps): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <SearchBar onChange={onSearchChange} value={searchQuery} />

      <AnimatedHeight>
        {debouncedQuery ? (
          <div className="animate-in fade-in duration-250" key="search">
            <JumpToResults onBrowseSpace={onBrowseSpace} onClose={onClose} query={debouncedQuery} />
          </div>
        ) : (
          <div className="animate-in fade-in duration-250" key={panel}>
            {panel === 'spaces' && <TenantRow onClose={onClose} />}

            {panel === 'spaces' ? (
              <SpacesPanel
                backendUser={backendUser}
                canReadSpaces={canReadSpaces}
                onBrowseSpace={onBrowseSpace}
                onClose={onClose}
              />
            ) : (
              <SitesPanel
                canReadSites={canReadSites}
                canSelectSpace={canSelectSpace}
                isSingleSpaceImposed={isSingleSpaceImposed}
                onBack={onGoBackToSpaces}
                onClose={onClose}
                spaceId={browsedSpaceId}
                spaceName={browsedSpaceName}
              />
            )}
          </div>
        )}
      </AnimatedHeight>
    </div>
  );
}
